import { memo, useCallback } from 'react'
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom'
import { useTranslations } from '@/core/translation'
import { ROUTES, usePackageInfo } from '@/utils'
import { Box } from '@/components/Layout'
import { Divider, Text } from '@/components/Data'
import { useToast } from '@/components'
import { AirlineOptions, FlightClassInfo, useRemoveFlightMutation } from '@/redux/package'
import { summarySidebarStyles as styles } from '../SummarySidebar/SummarySidebar.style'
import { SummaryCardActions } from '@/components/Composed'
import FlightInfo from '../FlightInfo'
import useUpdatePackageInfo from '@/utils/hooks/useUpdatePackageInfo'
import { useAnalytics, CART_TYPE } from '@/core/analytics'

const FlightSummary = () => {
  const t = useTranslations('flights')
  const tGeneral = useTranslations()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentPackage, isFlightModify, isSingleProduct } = usePackageInfo()
  const { refetchPackageInfo } = useUpdatePackageInfo()

  const analytics = useAnalytics()

  const [removeFlight] = useRemoveFlightMutation()

  const { sessionId = '', flightParams, flightInfo } = currentPackage ?? {}

  const onFlightEditClick = useCallback(() => {
    navigate({
      pathname: ROUTES.PACKAGE.FLIGHTS_DEPARTURE,
      search: createSearchParams({
        sessionId,
        modify: 'true',
        redirectUrl: location.pathname,
      }).toString(),
    })
  }, [navigate, sessionId, location.pathname])

  const containerStyle = { ...styles.addOnCard, ...styles.flight.flightInfoWrapper }

  const onFlightRemoveClick = useCallback(async () => {
    try {
      await removeFlight({ sessionId }).unwrap()
      refetchPackageInfo(sessionId)
      analytics.sendRemoveFromCartEvent(CART_TYPE.FLIGHT)
    } catch {
      toast(tGeneral('general.apiError'), 'error')
    }
  }, [removeFlight, sessionId, refetchPackageInfo, analytics, toast, tGeneral])

  if (!flightParams || !flightInfo) return null

  const { departure: departureFlight, return: returnFlight, classInfo } = flightInfo
  const hasReturnFlight = Object.keys(returnFlight).length > 0

  const departureAirline = getAirlineDetails(departureFlight.operatingAirline, flightInfo.airlines)
  const departureAirlineName = departureAirline?.name ?? departureFlight.operatingAirline
  const returnAirline = getAirlineDetails(returnFlight.operatingAirline, flightInfo.airlines)
  const returnAirlineName = returnAirline?.name ?? returnFlight.operatingAirline
  const departureFlightClass = getClassName(departureFlight.classInfo, classInfo)
  const returnFlightClass = hasReturnFlight ? getClassName(returnFlight.classInfo, classInfo) : ''

  return (
    <Box sx={{ ...containerStyle }}>
      <Text sx={styles.flight.airFare}>{t('airfare')}</Text>
      <Box>
        <Text sx={styles.flight.flightTitle}>{t('departureFlight')}</Text>
        <Box sx={styles.flight.flightDetailsWrapper}>
          <Text
            sx={
              styles.flight.airlineDetailsText
            }>{`${departureAirlineName} | ${t('class', { class: departureFlightClass })}`}</Text>
        </Box>
      </Box>
      <FlightInfo
        airport={{
          departure: departureFlight.airport.departure,
          destination: departureFlight.airport.destination,
        }}
        departureDateTime={departureFlight.departureDateTime}
        arrivalDateTime={departureFlight.arrivalDateTime}
        duration={departureFlight.duration}
        stops={departureFlight.stops}
        smallFonts
      />
      {hasReturnFlight && (
        <>
          <Divider sx={styles.flight.flightInfoDivider} />
          <Box>
            <Text sx={styles.flight.flightTitle}>{t('returnFlight')}</Text>
            <Box sx={styles.flight.flightDetailsWrapper}>
              <Text
                sx={
                  styles.flight.airlineDetailsText
                }>{`${returnAirlineName} | ${t('class', { class: returnFlightClass })}`}</Text>
            </Box>
          </Box>
          <FlightInfo
            airport={{
              departure: returnFlight.airport.departure,
              destination: returnFlight.airport.destination,
            }}
            departureDateTime={returnFlight.departureDateTime}
            arrivalDateTime={returnFlight.arrivalDateTime}
            duration={returnFlight.duration}
            stops={returnFlight.stops}
            smallFonts
          />
        </>
      )}
      <SummaryCardActions
        onEditClick={onFlightEditClick}
        onRemoveClick={onFlightRemoveClick}
        isActionDisabled={isFlightModify}
        isRemoveActionDisabled={isSingleProduct}
      />
    </Box>
  )
}

export default memo(FlightSummary)

function getClassName(classInfo: FlightClassInfo, defaultClassInfo: FlightClassInfo) {
  return (
    classInfo?.name ?? classInfo?.class ?? defaultClassInfo?.name ?? defaultClassInfo?.class ?? ''
  )
}

// Local mirror of `getAirlineDetails` from the Next.js app's
// `@/modules/Package/Flights/.../FlightListItem.utils` — inlined to avoid
// pulling in an out-of-scope module during the component-only migration.
function getAirlineDetails(code: string, airlines: AirlineOptions) {
  if (!code || !airlines) return null

  const normalizedCode = code.toUpperCase()
  return airlines[normalizedCode] || airlines[code] || null
}
